import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { tenantGetApiKeys as sdkGetApiKeys, tenantCreateApiKey as sdkCreateApiKey, tenantDeleteApiKey as sdkDeleteApiKey, tenantUpdateApiKeyStatus as sdkUpdateApiKeyStatus } from '@/lib/api/generated/sdk.gen'

declare const __VITE_API_BASE__: string;

export interface TenantSettings {
  blacklist: {
    phones: string[]
    keywords: string[]
  }
  webhook: {
    enabled: boolean
    url: string
    secret: string
    events: string[]
  }
  security: {
    enableIpWhitelist: boolean
    ipWhitelist: string[]
    enableRateLimit: boolean
    rateLimit: number
    dailyLimit: number
    allowedTimeStart: string
    allowedTimeEnd: string
  }
  api: {
    keys: Array<{
      id: string
      name: string
      key: string
      permissions: string[]
      status: 'ACTIVE' | 'INACTIVE'
      createdAt: string
      lastUsedAt?: string
    }>
  }
}

// 获取设置
export const useSettings = () => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<TenantSettings> => {
      // Placeholder endpoint backed by backend TenantSettingsController
      const res: any = await fetch(`${__VITE_API_BASE__ || ''}/api/v1/tenant/settings`, { credentials: 'include' })
      return res.json()
    },
  })
}

// 更新设置
export const useUpdateSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ section, data }: { section: string; data: any }): Promise<TenantSettings> => {
      const res = await fetch(`${__VITE_API_BASE__ || ''}/api/v1/tenant/settings/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
      })
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

// 创建API密钥
export const useCreateApiKey = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { name: string; permissions: string[] }): Promise<{ key: string }> => {
      const res: any = await sdkCreateApiKey({ client: apiClient, responseStyle: 'fields', throwOnError: false, body: {
        name: data.name,
        permissions: data.permissions,
      } as any })
      const payload: any = (res as any)?.data ?? res
      return { key: payload?.data?.appSecret || payload?.appSecret || payload?.secret || '' }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['settings', 'api-keys'] })
    },
  })
}

// 删除API密钥
export const useDeleteApiKey = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string | number): Promise<void> => {
      await sdkDeleteApiKey({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id: Number(id) } as any })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['settings', 'api-keys'] })
    },
  })
}

// 专门的 API Key 列表（供 Settings 使用）
export const useApiKeys = (params?: { page?: number; size?: number }) => {
  return useQuery({
    queryKey: ['settings', 'api-keys', params],
    queryFn: async () => {
      const res: any = await sdkGetApiKeys({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: { page: params?.page ?? 1, size: params?.size ?? 10 } as any })
      const payload: any = (res as any)?.data ?? res
      const list = (payload?.data ?? payload?.records ?? payload) as any[]
      return list
    },
  })
}